/**
 * Platform Admin — Provider Credentials (Credential Center / Secret Vault V1).
 *
 * HARD RULES (spec §9/§24):
 * - Never show/retrieve plaintext after submit
 * - No "Reveal / Copy existing key"
 * - Password input defaults to type=password; eye toggle only for unsubmitted input
 * - Clear React form state after successful create/rotate
 * - Never write secrets to localStorage / sessionStorage / URL / console
 */
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { Drawer, Input, InputNumber, Modal, Select, Space, Table, Tag, message } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import {
  platformActivateProviderCredentialVersion,
  platformCreateProviderCredential,
  platformDisableProviderCredential,
  platformEnableProviderCredential,
  platformGetProviderCredential,
  platformGetProviderOnboardingStatus,
  platformListCredentialProviders,
  platformListProviderCredentials,
  platformRotateProviderCredential,
  platformTestProviderCredential,
  platformUpdateProviderCredentialEnvironment,
} from '../api'
import type {
  ProviderCredentialEnvironment,
  ProviderCredentialRow,
  ProviderDefinitionRow,
  ProviderOnboardingStatus,
  TestConnectionResultCode,
} from '../types'
import { SectionCard, formatDateTime, formatRelativeTime } from './adminShared'
import { platformColors } from './theme'

const STATUS_TONE: Record<string, { color: string; label: string }> = {
  PENDING: { color: 'default', label: 'Pending' },
  ACTIVE: { color: 'success', label: 'Active' },
  DISABLED: { color: 'warning', label: 'Disabled' },
}

const HEALTH_TONE: Record<string, { color: string; label: string }> = {
  UNTESTED: { color: 'default', label: 'Untested' },
  HEALTHY: { color: 'success', label: 'Healthy' },
  INVALID: { color: 'error', label: 'Invalid' },
  UNAVAILABLE: { color: 'warning', label: 'Unavailable' },
}

const TEST_MESSAGE: Record<TestConnectionResultCode, string> = {
  VALID: 'Credential is valid',
  INVALID: 'Credential is invalid',
  RATE_LIMITED: 'Provider rate limit reached',
  BILLING_REQUIRED: 'Billing is required',
  UNAVAILABLE: 'Provider is temporarily unavailable',
  UNSUPPORTED_TEST: 'This provider has no low-cost Test Connection endpoint',
}

// VistoeraTASK-00173 (spec §13/§14) — the Credential Test's actual
// business result (never "did the HTTP request merely complete
// without throwing") decides exactly one toast severity/kind. Root
// cause of the previous "连接测试完成" (success) + "Provider is
// temporarily unavailable" (info) double-toast: the Test action used
// to go through the generic `runAction` helper below, which always
// fires `message.success(label)` once the wrapped promise resolves —
// even though `platformTestProviderCredential` always resolves with
// HTTP 200 and carries the REAL result inside the response body, not
// as an HTTP-level failure. `runTest` (not `runAction`) is the only
// path the Test button uses now.
const TEST_TOAST_KIND: Record<TestConnectionResultCode, 'success' | 'error' | 'warning' | 'info'> = {
  VALID: 'success',
  INVALID: 'error',
  RATE_LIMITED: 'warning',
  BILLING_REQUIRED: 'warning',
  UNAVAILABLE: 'warning',
  UNSUPPORTED_TEST: 'info',
}

function environmentScopeLabel(
  environment: string,
  t: (key: string) => string,
): string {
  if (environment === 'SHARED') return t('providers.credentials.scopeShared')
  if (environment === 'PREVIEW') return t('providers.credentials.scopePreviewOnly')
  if (environment === 'PRODUCTION') return t('providers.credentials.scopeProductionOnly')
  return environment
}

function formatCredentialMutationError(err: unknown): string {
  const e = err as { code?: string; status?: number; message?: string }
  if (e.code === 'ENCRYPTION_MASTER_KEY_MISSING') {
    return 'Credential storage configuration unavailable（缺少加密主密钥配置）'
  }
  if (e.status === 401) return '登录已失效，请重新登录'
  if (e.status === 403) return '当前账号无 Platform Admin 权限'
  if (e.status === 404) return e.message || 'Provider 或 Credential 不存在'
  if (e.status === 400 || e.status === 422) return e.message || 'Credential 字段不完整或不合法'
  if (e.status === 409) return e.message || '已存在同名 Credential'
  if (typeof e.status === 'number' && e.status >= 500) {
    return e.message || '保存 Credential 失败，请联系管理员'
  }
  if (e.status == null) {
    return e.message || '网络连接失败，请检查网络后重试'
  }
  return e.message || '操作失败'
}

function SecretField({
  value,
  onChange,
  label,
}: {
  value: string
  onChange: (v: string) => void
  label: string
}) {
  const [visible, setVisible] = useState(false)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, color: platformColors.secondaryText }}>{label}</label>
      <Input
        type={visible ? 'text' : 'password'}
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        suffix={
          <button
            type="button"
            aria-label={visible ? 'Hide secret' : 'Show secret'}
            onClick={() => setVisible((v) => !v)}
            style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 0, color: platformColors.mutedText }}
          >
            {visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          </button>
        }
      />
    </div>
  )
}

function CreateOrRotateModal({
  open,
  mode,
  providers,
  credential,
  onClose,
  onDone,
}: {
  open: boolean
  mode: 'create' | 'rotate'
  providers: ProviderDefinitionRow[]
  credential: ProviderCredentialRow | null
  onClose: () => void
  onDone: () => void
}) {
  const { t } = useTranslation('platformAdmin')
  const [providerKey, setProviderKey] = useState<string>('')
  const [name, setName] = useState('')
  const [accountLabel, setAccountLabel] = useState('')
  const [priority, setPriority] = useState<number | undefined>(undefined)
  const [environment, setEnvironment] = useState<ProviderCredentialEnvironment>('SHARED')
  const [secretFields, setSecretFields] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  const selectedProvider = useMemo(
    () => providers.find((p) => p.provider_key === providerKey) ?? null,
    [providers, providerKey],
  )

  const schemaEntries = useMemo(() => {
    const schema = selectedProvider?.credential_schema
    if (!schema) return [{ key: 'api_key', label: 'API Key', required: true }]
    return Object.entries(schema).map(([key, spec]) => ({
      key,
      label: spec.label || key,
      required: !!spec.required,
    }))
  }, [selectedProvider])

  useEffect(() => {
    if (!open) return
    if (mode === 'rotate' && credential) {
      setProviderKey(credential.provider_key || '')
      setName(credential.name)
      setEnvironment(credential.environment)
      setSecretFields({})
    } else {
      const first = providers.find((p) => p.credential_required)
      setProviderKey(first?.provider_key || '')
      setName('')
      setAccountLabel('')
      setPriority(undefined)
      setEnvironment('SHARED')
      setSecretFields({})
    }
  }, [open, mode, credential, providers])

  const clearPlaintext = () => setSecretFields({})

  const submit = async () => {
    const secret: Record<string, string> = {}
    for (const field of schemaEntries) {
      const v = (secretFields[field.key] || '').trim()
      if (field.required && !v) {
        message.error(t('providers.credentials.messages.fieldRequired', { label: field.label }))
        return
      }
      if (v) secret[field.key] = v
    }
    setSubmitting(true)
    try {
      if (mode === 'create') {
        if (!name.trim()) {
          message.error(t('providers.credentials.messages.nameRequired'))
          return
        }
        await platformCreateProviderCredential({
          provider_key: providerKey,
          name: name.trim(),
          environment,
          secret,
          account_label: accountLabel.trim() || undefined,
          priority,
        })
        message.success(t('providers.credentials.messages.created'))
      } else if (credential) {
        await platformRotateProviderCredential(credential.id, secret)
        message.success(t('providers.credentials.messages.rotated'))
      }
      clearPlaintext()
      onDone()
      onClose()
    } catch (err: unknown) {
      message.error(formatCredentialMutationError(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      title={
        mode === 'create'
          ? t('providers.credentials.modal.createTitle')
          : t('providers.credentials.modal.rotateTitle')
      }
      onCancel={() => {
        clearPlaintext()
        onClose()
      }}
      onOk={submit}
      confirmLoading={submitting}
      okButtonProps={{ disabled: submitting }}
      okText={
        mode === 'create'
          ? t('providers.credentials.modal.create')
          : t('providers.credentials.modal.rotateSubmit')
      }
      cancelText={t('providers.credentials.modal.cancel')}
      destroyOnClose
      maskClosable={!submitting}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 12, color: platformColors.secondaryText }}>
          {t('providers.credentials.modal.secretNotice')}
        </p>
        {mode === 'create' && (
          <>
            <div>
              <label style={{ fontSize: 12, color: platformColors.secondaryText }}>
                {t('providers.credentials.provider')}
              </label>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                value={providerKey || undefined}
                onChange={(next) => {
                  // VistoeraTASK-00173 (spec §7/§19 TEST 4) — switching
                  // Provider inside an already-open Create modal must
                  // NOT retain the previous Provider's secret draft.
                  // Several Providers' schemas happen to share the
                  // exact same field key (e.g. "api_key"), so without
                  // this the previous Provider's typed secret would
                  // silently carry over and be submittable against the
                  // newly-selected Provider.
                  setProviderKey(next)
                  setSecretFields({})
                }}
                options={providers
                  .filter((p) => p.credential_required)
                  .map((p) => ({ value: p.provider_key, label: `${p.display_name} (${p.provider_key})` }))}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: platformColors.secondaryText }}>
                {t('providers.credentials.accountName')}
              </label>
              <Input
                style={{ marginTop: 4 }}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('providers.credentials.accountNamePlaceholder')}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: platformColors.secondaryText }}>Account Label</label>
              <Input
                style={{ marginTop: 4 }}
                value={accountLabel}
                onChange={(e) => setAccountLabel(e.target.value)}
                placeholder="company / personal（仅 Platform Admin 可见）"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: platformColors.secondaryText }}>Priority</label>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={1}
                max={9999}
                value={priority}
                onChange={(v) => setPriority(typeof v === 'number' ? v : undefined)}
                placeholder="留空则自动递增（10, 20, …）"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: platformColors.secondaryText }}>
                {t('providers.credentials.environmentScope')}
              </label>
              <div data-testid="credential-environment-scope-select">
                <Select
                  style={{ width: '100%', marginTop: 4 }}
                  value={environment}
                  onChange={setEnvironment}
                  options={[
                    { value: 'SHARED', label: t('providers.credentials.scopeShared') },
                    { value: 'PREVIEW', label: t('providers.credentials.scopePreviewOnly') },
                    { value: 'PRODUCTION', label: t('providers.credentials.scopeProductionOnly') },
                  ]}
                />
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: platformColors.mutedText }}>
                {t('providers.credentials.environmentScopeHelp')}
              </p>
            </div>
          </>
        )}
        {schemaEntries.map((field) => (
          <SecretField
            key={field.key}
            label={`${field.label}${field.required ? ' *' : ''}`}
            value={secretFields[field.key] || ''}
            onChange={(v) => setSecretFields((prev) => ({ ...prev, [field.key]: v }))}
          />
        ))}
      </div>
    </Modal>
  )
}

export default function ProviderCredentialsPage() {
  const { t } = useTranslation('platformAdmin')
  const [searchParams] = useSearchParams()
  const providerFilter = searchParams.get('provider_key') || undefined

  const [rows, setRows] = useState<ProviderCredentialRow[]>([])
  const [providers, setProviders] = useState<ProviderDefinitionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [rotateTarget, setRotateTarget] = useState<ProviderCredentialRow | null>(null)
  const [detail, setDetail] = useState<ProviderCredentialRow | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  // VistoeraTASK-00177 §7 — "接入状态" (Provider Onboarding Status) panel.
  // Fetched alongside the credential detail, never blocks/replaces the
  // existing Health tag — Credential Healthy and Provider Onboarding are
  // deliberately separate, independently-visible facts (task spec §1/§6).
  const [onboarding, setOnboarding] = useState<ProviderOnboardingStatus | null>(null)
  const [onboardingLoading, setOnboardingLoading] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([
      platformListProviderCredentials(providerFilter),
      platformListCredentialProviders(),
    ])
      .then(([credRes, provRes]) => {
        setRows(credRes.data.credentials ?? [])
        setProviders(provRes.data.providers ?? [])
      })
      .catch((err) => message.error(err?.message || t('providers.credentials.messages.loadFailed')))
      .finally(() => setLoading(false))
  }, [providerFilter, t])

  useEffect(() => {
    load()
  }, [load])

  const loadOnboardingStatus = async (providerKey: string | null | undefined) => {
    if (!providerKey) {
      setOnboarding(null)
      return
    }
    setOnboardingLoading(true)
    try {
      const res = await platformGetProviderOnboardingStatus(providerKey)
      setOnboarding(res.data)
    } catch {
      setOnboarding(null)
    } finally {
      setOnboardingLoading(false)
    }
  }

  const openDetail = async (id: string) => {
    setDetailLoading(true)
    try {
      const res = await platformGetProviderCredential(id)
      setDetail(res.data)
      void loadOnboardingStatus(res.data.provider_key)
    } catch (err: unknown) {
      message.error((err as Error)?.message || t('providers.credentials.messages.detailFailed'))
    } finally {
      setDetailLoading(false)
    }
  }

  const runAction = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn()
      message.success(label)
      load()
      if (detail) {
        const refreshed = await platformGetProviderCredential(detail.id)
        setDetail(refreshed.data)
      }
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string }
      if (e.code === 'CREDENTIAL_SCOPE_CONFLICT') {
        message.error(t('providers.credentials.messages.scopeConflict'))
        return
      }
      message.error(e.message || t('providers.credentials.messages.actionFailed'))
    }
  }

  // VistoeraTASK-00173 — dedicated Test Connection handler. Deliberately
  // NOT `runAction`: this call always resolves HTTP 200 with the real
  // business outcome inside `res.data.result`, so `runAction`'s
  // unconditional post-await `message.success()` would fire regardless
  // of whether the credential is actually valid, invalid, or the
  // Provider is unavailable — exactly the double-toast/contradictory-
  // status bug this fixes. Exactly one toast, chosen by the actual
  // result code.
  const runTest = async (credentialId: string) => {
    try {
      const res = await platformTestProviderCredential(credentialId)
      const code = res.data.result
      const text = TEST_MESSAGE[code] || res.data.message
      const kind = TEST_TOAST_KIND[code] || 'info'
      message[kind](text)
      load()
      if (detail) {
        const refreshed = await platformGetProviderCredential(detail.id)
        setDetail(refreshed.data)
      }
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string }
      message.error(e.message || t('providers.credentials.messages.actionFailed'))
    }
  }

  return (
    <div data-testid="credentials-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: platformColors.primaryText }}>
            {t('providers.credentials.pageTitle')}
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: platformColors.secondaryText, maxWidth: 680 }}>
            {t('providers.credentials.pageSubtitle')}
            {providerFilter
              ? ` ${t('providers.credentials.filterActive', { provider: providerFilter })}`
              : ''}
          </p>
        </div>
        <Space>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 32,
              padding: '0 12px',
              borderRadius: 6,
              border: `1px solid ${platformColors.border}`,
              background: platformColors.surface,
              fontSize: 13,
              cursor: loading ? 'default' : 'pointer',
            }}
          >
            <ReloadOutlined spin={loading} /> {t('providers.credentials.refresh')}
          </button>
          <button
            type="button"
            data-testid="add-credential-button"
            onClick={() => setCreateOpen(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 32,
              padding: '0 12px',
              borderRadius: 6,
              border: 'none',
              background: platformColors.primary,
              color: '#fff',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <PlusOutlined /> {t('providers.credentials.addCredential')}
          </button>
        </Space>
      </div>

      <SectionCard title={t('providers.credentials.sectionTitle')}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          pagination={false}
          size="middle"
          onRow={(row) => ({
            onClick: () => openDetail(row.id),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: t('providers.credentials.columns.name'),
              dataIndex: 'name',
              render: (name: string, row) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{name}</div>
                  <div style={{ fontSize: 11, color: platformColors.mutedText }}>
                    {row.provider_display_name || row.provider_key}
                    {row.is_default ? ' · Default' : ''}
                  </div>
                </div>
              ),
            },
            {
              title: 'Priority',
              dataIndex: 'priority',
              width: 90,
              render: (v: number | undefined) => v ?? '—',
            },
            {
              title: 'Account',
              dataIndex: 'account_label',
              width: 120,
              render: (v: string | null) => v || <span style={{ color: platformColors.mutedText }}>—</span>,
            },
            {
              title: t('providers.credentials.columns.environmentScope'),
              dataIndex: 'environment',
              width: 200,
              render: (v: string) => <Tag>{environmentScopeLabel(v, t)}</Tag>,
            },
            {
              title: t('providers.credentials.columns.accessScope'),
              dataIndex: 'scope_type',
              width: 120,
            },
            {
              title: t('providers.credentials.columns.status'),
              dataIndex: 'status',
              width: 110,
              render: (s: string) => {
                const tone = STATUS_TONE[s] || { color: 'default', label: s }
                return <Tag color={tone.color}>{tone.label}</Tag>
              },
            },
            {
              title: t('providers.credentials.columns.secret'),
              dataIndex: 'masked_hint',
              width: 160,
              render: (v: string | null) => (
                <code style={{ fontSize: 12, color: platformColors.primaryText }}>{v || '—'}</code>
              ),
            },
            {
              title: t('providers.credentials.columns.health'),
              dataIndex: 'health_status',
              width: 110,
              render: (h: string) => {
                const tone = HEALTH_TONE[h] || { color: 'default', label: h }
                return <Tag color={tone.color}>{tone.label}</Tag>
              },
            },
            {
              title: t('providers.credentials.columns.lastUpdated'),
              dataIndex: 'last_tested_at',
              width: 120,
              render: (v: string | null) => formatRelativeTime(v),
            },
            {
              title: t('providers.credentials.columns.actions'),
              width: 280,
              render: (_: unknown, row: ProviderCredentialRow) => (
                <Space size={4} onClick={(e) => e.stopPropagation()}>
                  <a onClick={() => void runTest(row.id)}>
                    {t('providers.credentials.actions.test')}
                  </a>
                  <a onClick={() => setRotateTarget(row)}>{t('providers.credentials.actions.rotate')}</a>
                  {row.status === 'DISABLED' ? (
                    <a
                      onClick={() =>
                        runAction(t('providers.credentials.messages.enabled'), () =>
                          platformEnableProviderCredential(row.id),
                        )
                      }
                    >
                      {t('providers.credentials.actions.enable')}
                    </a>
                  ) : (
                    <a
                      onClick={() =>
                        runAction(t('providers.credentials.messages.disabled'), () =>
                          platformDisableProviderCredential(row.id),
                        )
                      }
                    >
                      {t('providers.credentials.actions.disable')}
                    </a>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </SectionCard>

      <CreateOrRotateModal
        open={createOpen}
        mode="create"
        providers={providers}
        credential={null}
        onClose={() => setCreateOpen(false)}
        onDone={load}
      />
      <CreateOrRotateModal
        open={!!rotateTarget}
        mode="rotate"
        providers={providers}
        credential={rotateTarget}
        onClose={() => setRotateTarget(null)}
        onDone={load}
      />

      <Drawer
        title={detail?.name || t('providers.credentials.pageTitle')}
        open={!!detail}
        onClose={() => setDetail(null)}
        width={520}
        loading={detailLoading}
      >
        {detail && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8, fontSize: 13 }}>
              <div style={{ color: platformColors.secondaryText }}>{t('providers.credentials.detail.provider')}</div>
              <div>{detail.provider_display_name || detail.provider_key}</div>
              <div style={{ color: platformColors.secondaryText }}>{t('providers.credentials.detail.accessScope')}</div>
              <div>{detail.scope_type}</div>
              <div style={{ color: platformColors.secondaryText }}>
                {t('providers.credentials.detail.environmentScope')}
              </div>
              <div>
                <Select
                  data-testid="credential-detail-environment-scope"
                  style={{ width: '100%', maxWidth: 320 }}
                  value={detail.environment}
                  options={[
                    { value: 'SHARED', label: t('providers.credentials.scopeShared') },
                    { value: 'PREVIEW', label: t('providers.credentials.scopePreviewOnly') },
                    { value: 'PRODUCTION', label: t('providers.credentials.scopeProductionOnly') },
                  ]}
                  onChange={(next) => {
                    void runAction(t('providers.credentials.messages.environmentUpdated'), () =>
                      platformUpdateProviderCredentialEnvironment(detail.id, next),
                    )
                  }}
                />
                <div style={{ marginTop: 4, fontSize: 12, color: platformColors.mutedText }}>
                  {t('providers.credentials.environmentScopeHelp')}
                </div>
              </div>
              <div style={{ color: platformColors.secondaryText }}>{t('providers.credentials.detail.status')}</div>
              <div>{STATUS_TONE[detail.status]?.label || detail.status}</div>
              <div style={{ color: platformColors.secondaryText }}>{t('providers.credentials.detail.secret')}</div>
              <div>
                <code>{detail.masked_hint || '—'}</code>
              </div>
              <div style={{ color: platformColors.secondaryText }}>{t('providers.credentials.detail.health')}</div>
              <div>{HEALTH_TONE[detail.health_status]?.label || detail.health_status}</div>
              <div style={{ color: platformColors.secondaryText }}>{t('providers.credentials.detail.created')}</div>
              <div>{formatDateTime(detail.created_at)}</div>
              <div style={{ color: platformColors.secondaryText }}>{t('providers.credentials.detail.lastTested')}</div>
              <div>{formatDateTime(detail.last_tested_at)}</div>
              <div style={{ color: platformColors.secondaryText }}>{t('providers.credentials.detail.lastUsed')}</div>
              <div>{formatDateTime(detail.last_used_at)}</div>
            </div>

            <SectionCard title="Provider 接入状态">
              {onboardingLoading ? (
                <div style={{ color: platformColors.mutedText, fontSize: 12 }}>加载中…</div>
              ) : onboarding ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 6, columnGap: 8 }}>
                    <div style={{ color: platformColors.secondaryText }}>Credential</div>
                    <div>
                      <Tag color={onboarding.credential_healthy ? 'success' : 'default'}>
                        {onboarding.credential_healthy ? '✓ Healthy' : onboarding.credential_configured ? '⚠ Unhealthy' : '— Not configured'}
                      </Tag>
                    </div>
                    <div style={{ color: platformColors.secondaryText }}>Models</div>
                    <div>
                      {onboarding.model_discovery_status === 'NOT_APPLICABLE' ? (
                        <Tag>— 无动态发现机制</Tag>
                      ) : onboarding.model_count > 0 ? (
                        <Tag color="success">✓ {onboarding.model_count} discovered</Tag>
                      ) : (
                        <Tag color="warning">⚠ 0 discovered</Tag>
                      )}
                    </div>
                    <div style={{ color: platformColors.secondaryText }}>Enabled Models</div>
                    <div>
                      <Tag color={onboarding.enabled_model_count > 0 ? 'success' : 'default'}>
                        {onboarding.enabled_model_count > 0 ? `✓ ${onboarding.enabled_model_count}` : '—'}
                      </Tag>
                    </div>
                    <div style={{ color: platformColors.secondaryText }}>Capabilities</div>
                    <div>
                      <Tag color={onboarding.capability_ready_count > 0 ? 'success' : 'default'}>
                        {onboarding.capability_ready_count > 0 ? `✓ ${onboarding.capability_ready_count} ready` : '—'}
                      </Tag>
                    </div>
                    <div style={{ color: platformColors.secondaryText }}>Function Nodes</div>
                    <div>
                      <Tag color={onboarding.function_node_binding_count > 0 ? 'success' : 'default'}>
                        {onboarding.function_node_binding_count > 0 ? `✓ ${onboarding.function_node_binding_count}` : '—'}
                      </Tag>
                    </div>
                    <div style={{ color: platformColors.secondaryText }}>Provider Status</div>
                    <div>
                      <Tag color={onboarding.tenant_available ? 'success' : 'warning'}>
                        {onboarding.tenant_available ? 'Ready' : 'Incomplete'}
                      </Tag>
                    </div>
                  </div>
                  {!onboarding.tenant_available && (
                    <div style={{ color: platformColors.mutedText, fontSize: 12, lineHeight: 1.6 }}>
                      {onboarding.blocking_reason === 'CREDENTIAL_NOT_CONFIGURED' && '尚未配置 Credential。'}
                      {onboarding.blocking_reason === 'CREDENTIAL_UNHEALTHY' && '凭据尚未通过健康检查，请先完成 Test Connection。'}
                      {onboarding.blocking_reason === 'ZERO_MODELS_DISCOVERED' &&
                        '凭据验证成功，但未发现任何可用模型 → 请前往「模型管理」重试同步 / 查看错误。'}
                      {onboarding.blocking_reason === 'NO_MODELS_ENABLED' &&
                        `已发现 ${onboarding.model_count} 个模型，但尚未启用任何模型 → 请前往「模型管理」启用。`}
                      {onboarding.blocking_reason === 'CAPABILITY_NOT_READY' &&
                        `${onboarding.enabled_model_count} 个模型待能力确认 → 请前往「模型能力」同步。`}
                      {onboarding.blocking_reason === 'NOT_BOUND_TO_FUNCTION_NODE' &&
                        '模型能力已就绪，但尚未绑定任何功能节点 → 请前往「功能节点管理」添加模型。'}
                      {onboarding.blocking_reason === 'NO_EXECUTION_PLAN' &&
                        '已绑定功能节点，但尚无可用 Execution Plan → 请前往「功能节点管理」新增执行方案。'}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: platformColors.mutedText, fontSize: 12 }}>接入状态加载中或不可用。</div>
              )}
            </SectionCard>

            <SectionCard title={t('providers.credentials.detail.versions')}>
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={detail.versions || []}
                columns={[
                  { title: 'v', dataIndex: 'version_number', width: 48 },
                  {
                    title: t('providers.credentials.columns.secret'),
                    dataIndex: 'masked_hint',
                    render: (v: string) => <code style={{ fontSize: 12 }}>{v}</code>,
                  },
                  { title: t('providers.credentials.columns.status'), dataIndex: 'status', width: 100 },
                  {
                    title: '',
                    width: 90,
                    render: (_: unknown, ver) =>
                      ver.is_active ? (
                        <Tag color="success">Active</Tag>
                      ) : ver.status === 'REVOKED' ? null : (
                        <a
                          onClick={() =>
                            runAction(
                              t('providers.credentials.messages.activated', { version: ver.version_number }),
                              () => platformActivateProviderCredentialVersion(detail.id, ver.id),
                            )
                          }
                        >
                          {t('providers.credentials.actions.activate')}
                        </a>
                      ),
                  },
                ]}
              />
            </SectionCard>
          </div>
        )}
      </Drawer>
    </div>
  )
}
